import { inject, Injectable } from '@angular/core';
import { Bundle, MedicationRequest, MedicationDispense } from 'fhir/r4';
import { catchError, map, Observable, of, shareReplay, throwError } from 'rxjs';
import { StateService } from '../shared/state.service';
import { HttpClient } from '@angular/common/http';
import { backendEndPointToken } from '../app.config';

@Injectable({
  providedIn: 'root'
})
export class RequestsHelperService {
  dispenseRecordHolder?: MedicationDispense;
  constructor() { }

  stateService = inject(StateService);
  http = inject(HttpClient);
  backendApiEndpoint = inject(backendEndPointToken);

  getAllDispenseRecords(medRow: MedicationRequest & Record<string, any>): Observable<Bundle> {
    // let patientObs = this.stateService.currentPatientId$.getValue();

    // return this.stateService.currentPatientId$.pipe(

    let patientId = (medRow.subject?.reference?.split("/")[medRow.subject?.reference?.split("/").length - 1] ?? null);
    console.log('Fetching MedicationDispense for Patient/', patientId);
    if (!patientId) {
      return throwError(() => new Error('MedicationRequest.subject.reference is missing or invalid'));
    }
    return this.http
      .get<Bundle>(`${this.backendApiEndpoint}/MedicationDispense?subject=Patient/${patientId}`)
      .pipe(
        catchError((err) => {
          console.error('Failed to fetch MedicationDispense bundle', err);
          return of({ resourceType: 'Bundle', type: 'searchset', entry: [] } as unknown as Bundle);
        }),
        shareReplay({ bufferSize: 1, refCount: true })
      );

  }

  /**
   * Returns:
   * - isDispensable: can user start a new dispense now?
   * - hasDispenseInProgress: there is an active dispense for this MR
   * - dispenseInProgressId: the MedicationDispense.id of the active dispense (if any)
   * - isDispenseCompleted: at least one completed dispense exists for this MR
   */
  getDispenseStateForRequest(
    medRow: MedicationRequest & Record<string, any>
  ): Observable<{
    isDispensable: boolean;
    hasDispenseInProgress: boolean;
    dispenseInProgressId: string | null;
    isDispenseCompleted: boolean;
  }> {
    const mrStatus = (medRow?.status ?? '').toString().toLowerCase();

    // Terminal MR => not dispensable regardless of dispenses
    if (['completed', 'cancelled', 'entered-in-error'].includes(mrStatus)) {
      return of({
        isDispensable: false,
        hasDispenseInProgress: false,
        dispenseInProgressId: null,
        isDispenseCompleted: mrStatus === 'completed'
      });
    }

    if (mrStatus === 'draft') {
      return of({
        isDispensable: false,
        hasDispenseInProgress: false,
        dispenseInProgressId: null,
        isDispenseCompleted: false
      });
    }

    return this.getAllDispenseRecords(medRow).pipe(
      map((bundle) => {
        const resources = (bundle?.entry ?? []).map(e => e.resource);

        const dispensesForRequest = resources
          .filter((r: any): r is MedicationDispense => r?.resourceType === 'MedicationDispense')
          .filter((dispense) =>
            (dispense.authorizingPrescription ?? []).some(ref => ref?.reference === `MedicationRequest/${medRow.id}`)
          );

        const normalizeStatus = (s: any) => (s ?? '').toString().toLowerCase();

        const isDispenseCompleted = dispensesForRequest.some(d => normalizeStatus(d.status) === 'completed');
// $#$        // /* alert(isDispenseCompleted); */
        console.log('Dispenses for MR', medRow.id, dispensesForRequest);
        // define "in progress" as not in terminal states
        const inProgressOrCompleted = dispensesForRequest.find(d => {
          const st = normalizeStatus(d.status);
          return ['preparation', 'completed', 'on-hold', 'in-progress'].includes(st);
        });

        const inProgress = dispensesForRequest.find(d => {
          const st = normalizeStatus(d.status);
          return ['preparation', 'on-hold', 'in-progress'].includes(st);
        });


        const hasDispenseInProgress = !!inProgress;
        const dispenseInProgressId = (inProgress?.id ? String(inProgress.id) : null);

        // business rule: you can start a new dispense only if none is active/in-progress
        const isDispensable = !inProgressOrCompleted;

        return {
          isDispensable,
          hasDispenseInProgress,
          dispenseInProgressId,
          isDispenseCompleted
        };
      }),
      catchError((err) => {
        console.error('Failed to evaluate dispense state for MedicationRequest', err);
        return of({
          isDispensable: false,
          hasDispenseInProgress: false,
          dispenseInProgressId: null,
          isDispenseCompleted: false
        });
      })
    );
  }

  // Backwards-compatible wrapper (optional): keep old callers working
  isNewDispenseAllowed(medRow: MedicationRequest & Record<string, any>): Observable<boolean> {
    return this.getDispenseStateForRequest(medRow).pipe(map(s => s.isDispensable));
  }
}
